import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { WorkspaceService } from '../../workspace';
import { useConfirmAppDataChange } from './app-data-change-confirmation';
import {
  type AppResourceLifecycleImpact,
  fetchDocAppResourceLifecycleImpacts,
  fetchFolderAppResourceLifecycleImpacts,
} from './app-resource-lifecycle';

export interface AppDataMoveTarget {
  id: string;
  kind: 'doc' | 'folder';
  targetFolderId: string | null;
}

/**
 * Warns before a page or folder moves while other apps read data hosted on
 * it. The rights of the host page decide what those apps can read, so the
 * move is allowed but announced first. Resolves when the move may proceed and
 * rejects with an `isAppDataChangeCancelled` error when the user cancels.
 */
export function useConfirmAppDataMove(): (
  target: AppDataMoveTarget
) => Promise<void> {
  const workspaceService = useService(WorkspaceService);
  const confirmAppDataChange = useConfirmAppDataChange();
  const t = useI18n();

  return useCallback(
    async (target: AppDataMoveTarget) => {
      const workspace = workspaceService.workspace;
      if (workspace.flavour === 'local') {
        return;
      }
      await confirmAppDataChange(await fetchMoveImpacts(workspace.id, target), {
        confirm: t['com.streamlain.appDataMove.confirm'](),
        description: t['com.streamlain.appDataMove.description'](),
        title: t['com.streamlain.appDataMove.title'](),
      });
    },
    [confirmAppDataChange, t, workspaceService]
  );
}

async function fetchMoveImpacts(
  workspaceId: string,
  target: AppDataMoveTarget
): Promise<AppResourceLifecycleImpact[]> {
  const fetchImpacts =
    target.kind === 'doc'
      ? fetchDocAppResourceLifecycleImpacts
      : fetchFolderAppResourceLifecycleImpacts;
  try {
    return await fetchImpacts(
      workspaceId,
      'move',
      [target.id],
      target.targetFolderId
    );
  } catch (error) {
    // Moves are local-first and replay offline. The warning is advisory; the
    // server stays authoritative for every rule, so an unreachable impact read
    // must not block the move.
    console.warn('App data move impacts unavailable', error);
    return [];
  }
}
